<template>
	<div class="permissions-container">
		<v-container class="groups-container">
			<v-row>
				<v-col cols="4">
					<v-autocomplete
						v-model="selectedGroupId"
						ref="LinkFieldId"
						:items="groups"
						item-text="Name"
						item-value="Id"
						label="Select group"
						class="group-selector"
						hide-details
					>
						<template v-slot:[`item`]="{ item }">
							<v-list-item-content
								:color="`${item.color} lighten-3`"
							>
								<v-list-item-title>{{
									item.Name
								}}</v-list-item-title>
							</v-list-item-content>
							<v-list-item-action @click.stop>
								<v-btn
									v-if="canEditGroups"
									fab
									small
									icon
									elevation="1"
									class="action-btn"
									@click.stop="openRenameGroup(item)"
								>
									<v-icon small color="primary"
										>mdi-pencil</v-icon
									>
								</v-btn>
							</v-list-item-action>
							<v-list-item-action @click.stop>
								<v-btn
									v-if="canDeleteGroups"
									fab
									small
									icon
									elevation="1"
									class="action-btn"
									@click.stop="deleteGroup(item)"
								>
									<v-icon small color="error"
										>mdi-delete</v-icon
									>
								</v-btn>
							</v-list-item-action>
						</template>
					</v-autocomplete>
				</v-col>
			</v-row>
		</v-container>
		<v-container class="search-container">
			<v-row>
				<v-col cols="10">
					<v-text-field
						v-model="search"
						class="py-0 my-0 mb-2"
						append-icon="mdi-magnify"
						label="Search permission"
						single-line
						hide-details
					/>
				</v-col>
				<v-col class="text-right">
					<v-btn
						v-if="canAddGroups"
						color="primary"
						@click="openAddGroup"
					>
						Add group
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
		<div class="permissions-section">
			<div class="all-permissions">
				<v-list>
					<v-subheader>All permissions</v-subheader>
					<v-list-item-group
						v-model="selectedNotApplied"
						active-class="primary white--text"
						class="field-list"
						multiple
					>
						<v-list-item
							v-for="permission in notAppliedPermissions"
							:key="permission.Id"
							:value="permission.Id"
						>
							<v-list-item-content>
								<v-list-item-title>{{
									permission.Name
								}}</v-list-item-title>
							</v-list-item-content>
						</v-list-item>
					</v-list-item-group>
				</v-list>
			</div>
			<div class="permission-actions">
				<v-btn
					color="primary"
					outlined
					text
					@click="addPermission"
					:disabled="!canEditGroups"
				>
					<v-icon> mdi-arrow-right </v-icon>
				</v-btn>
				<v-btn
					color="primary"
					outlined
					text
					@click="removePermission"
					:disabled="!canEditGroups"
				>
					<v-icon> mdi-arrow-left </v-icon>
				</v-btn>
			</div>
			<div class="applied-permissions">
				<v-list>
					<v-subheader>Applied permissions</v-subheader>
					<v-list-item-group
						v-model="selectedApplied"
						active-class="primary white--text"
						class="field-list"
						multiple
					>
						<v-list-item
							v-for="permission in appliedPermissions"
							:key="permission.Id"
							:value="permission.Id"
						>
							<v-list-item-content>
								<v-list-item-title>{{
									permission.Name
								}}</v-list-item-title>
							</v-list-item-content>
						</v-list-item>
					</v-list-item-group>
				</v-list>
			</div>
		</div>

		<add-edit-group-dialog
			:editedGroup="editedGroup"
			:dialog="showAddEditGroup"
			@saveGroup="saveGroup"
			@closeDialog="closeAddEditDialog"
		/>
		<manage-groups-dialog
			:dialog="showManageGroups"
			@closeDialog="showManageGroups = false"
		/>
	</div>
</template>

<script>
import AddEditGroupDialog from '@/components/core/permissions/AddEditGroupDialog';
import ManageGroupsDialog from '@/components/core/permissions/ManageGroupsDialog';
import { mapGetters } from 'vuex';
import { PermissionsGetters } from '@/store/getters';
import { PermissionsActions } from '@/store/actions';
import ConfirmDialog from '@/components/core/dialogs/confirmDialog';
import permissionsMixin from '@/mixins/permissionsMixin';

export default {
	name: 'Permissions',
	components: {
		ManageGroupsDialog,
		AddEditGroupDialog,
	},
	mixins: [permissionsMixin],
	data() {
		return {
			search: '',
			showAddEditGroup: false,
			showManageGroups: false,
			editedGroup: {},
			selectedGroupId: null,
			currentGroup: {},
			selectedNotApplied: [],
			selectedApplied: [],
		};
	},
	computed: {
		...mapGetters({
			allPermissions: [PermissionsGetters.GET_PERMISSIONS_LIST],
			groups: [PermissionsGetters.GET_PERMISSION_GROUPS],
		}),
		canAddGroups() {
			return this.hasPermissions(
				this.permissions.Settings.Permissions.PermissionsGroup.Add
			);
		},
		canEditGroups() {
			return this.hasPermissions(
				this.permissions.Settings.Permissions.PermissionsGroup.Edit
			);
		},
		canDeleteGroups() {
			return this.hasPermissions(
				this.permissions.Settings.Permissions.PermissionsGroup.Delete
			);
		},
		notAppliedPermissions() {
			if (!this.currentGroup.Id) return [];
			return this.allPermissions.filter(
				(permission) =>
					!this.appliedPermissions.find(
						(aPermission) => permission.Id === aPermission.Id
					) && permission.Name.includes(this.search)
			);
		},
		appliedPermissions() {
			if (!this.currentGroup.Id) return [];
			return this.currentGroup.Permissions.filter((permission) =>
				permission.Name.includes(this.search)
			);
		},
	},
	watch: {
		selectedGroupId(next) {
			this.$set(
				this,
				'currentGroup',
				JSON.parse(
					JSON.stringify(
						this.groups.find((group) => group.Id === next)
					)
				)
			);
		},
	},
	mounted() {
		this.$store.dispatch(PermissionsActions.LOAD_PERMISSIONS_LIST);
		this.$store.dispatch(PermissionsActions.LOAD_PERMISSION_GROUPS);
	},
	destroyed() {
		this.$store.dispatch(PermissionsActions.CLEAR_PERMISSIONS_PAGE);
	},
	methods: {
		openAddGroup() {
			this.editedGroup = {
				Name: '',
			};
			this.showAddEditGroup = true;
		},
		openRenameGroup(group) {
			this.editedGroup = { ...group };
			this.showAddEditGroup = true;
		},
		saveGroup(group) {
			if (group.Id) {
				this.$store.dispatch(PermissionsActions.UPDATE_GROUP, group);
			} else if (group.Name) {
				this.$store.dispatch(PermissionsActions.ADD_GROUP, group);
			}

			this.closeAddEditDialog();
		},
		deleteGroup(group) {
			ConfirmDialog({
				message: 'Are you sure you want to delete this Grid Field?',
			}).then((isDelete) => {
				if (isDelete) {
					this.$store.dispatch(
						PermissionsActions.DELETE_GROUP,
						group.Id
					);
				}
			});
		},
		closeAddEditDialog() {
			this.editedGroup = {};
			this.showAddEditGroup = false;
		},
		addPermission() {
			this.selectedNotApplied.forEach((permissionId) => {
				let newPermission = this.allPermissions.find(
					(permission) => permissionId === permission.Id
				);
				this.$set(
					this.currentGroup.Permissions,
					this.currentGroup.Permissions.length,
					newPermission
				);
			});
			this.selectedApplied = this.selectedNotApplied;
			this.selectedNotApplied = [];
			this.saveGroup(this.currentGroup);
		},
		removePermission() {
			this.selectedApplied.forEach((permissionId) => {
				let permissionIndex = this.currentGroup.Permissions.findIndex(
					(permission) => permission.Id === permissionId
				);
				this.$delete(this.currentGroup.Permissions, permissionIndex);
			});
			this.selectedNotApplied = this.selectedApplied;
			this.selectedApplied = [];
			this.saveGroup(this.currentGroup);
		},
	},
};
</script>

<style lang="scss" scoped>
.permissions-container {
	height: 100%;

	.groups-container {
		display: flex;
		justify-content: space-between;
		align-content: center;
	}

	.search-container {
		display: flex;
		justify-content: space-between;
		.v-btn {
			margin: 0 0.25rem;
		}
	}

	.permissions-section {
		display: flex;
		padding: 0 0.5rem 0.5rem 0.5rem;
		height: calc(100% - 137px);

		.all-permissions,
		.applied-permissions {
			width: 41.6%;
			border: 1px solid #eee;
			height: 100%;

			.v-list {
				padding: 0;
				height: 100%;

				.v-subheader {
					padding: 0 0.5rem;
				}

				.field-list {
					height: calc(100% - 48px);
					overflow-y: scroll;

					.v-list-item__title {
						font-weight: 400;
					}
				}
			}
		}

		.permission-actions {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			width: 16.6%;

			.v-btn {
				margin: 0.5rem 0;
			}
		}
	}
}
</style>
